import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAccess } from "../context/AccessContext";

export default function TutorialDetail() {
  const { id } = useParams();
  const [tut, setTut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token, unlocked } = useAccess();

  useEffect(() => {
    setLoading(true);
    api.post(`/tutorials/${id}`, { access_token: token })
      .then((r) => setTut(r.data))
      .catch(() => setError("Tutorial not found"))
      .finally(() => setLoading(false));
  }, [id, token]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-20"><div className="h-12 w-1/2 bg-[#F2EFEB] animate-pulse mb-6" /><div className="aspect-video bg-[#F2EFEB] animate-pulse" /></div>;
  }
  if (error || !tut) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="overline mb-3">404</div>
        <h1 className="font-serif-display text-4xl text-stone-900">Tutorial not found.</h1>
        <Link to="/tutorials" className="inline-block mt-8 text-xs uppercase tracking-[0.22em] text-[#8C6A5D]">← All tutorials</Link>
      </div>
    );
  }

  return (
    <div data-testid="tutorial-detail" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <Link to="/tutorials" className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900">
        ← All Tutorials
      </Link>
      <div className="mt-8">
        <div className="overline mb-3">{tut.level} · {tut.duration}</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight leading-[1.05]">{tut.title}</h1>
        <p className="mt-4 text-lg italic text-stone-600 font-light">{tut.tagline}</p>
      </div>

      <div className="mt-10 bg-[#2C2A29] aspect-video w-full overflow-hidden">
        {unlocked && tut.video_url ? (
          <iframe
            data-testid="tutorial-video"
            src={tut.video_url}
            title={tut.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center px-6">
            <div>
              <div className="overline mb-2" style={{ color: "#E8D5D1" }}>{!unlocked ? "Locked" : "Coming Soon"}</div>
              <h3 className="font-serif-display text-3xl text-stone-100 font-light">
                {!unlocked ? "Unlock the Tutorial Vault to watch." : "This video is being filmed."}
              </h3>
              {!unlocked && (
                <Link to="/tutorials" className="inline-block mt-6 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-6 py-3">
                  Unlock Tutorials
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="overline mb-3">In this lesson</div>
          <p className="text-base text-stone-700 leading-relaxed">{tut.summary}</p>
        </div>
        <aside>
          <div className="overline mb-3">Brushes used</div>
          <ul className="space-y-2">
            {(tut.covers_brushes || []).map((bid) => (
              <li key={bid}>
                <Link to={`/brush/${bid}`} className="text-sm text-stone-700 hover:text-stone-900 underline underline-offset-4 decoration-[#E8D5D1]">
                  {bid.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
